import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  actionSetLaborTickets,
  actionSetWorkOrderOperationLaborTickets,
} from 'lib/actions';
import { getMachine } from 'lib/selectors/getMachine';
import { getWorkOrderOperationLaborTickets } from 'lib/selectors/getWorkOrderOperationLaborTickets';
import useResyncMachineLaborTickets from 'lib/hooks/useResyncMachineLaborTickets';
import { dispatchNatsResponseActions } from 'lib/utils/handleNatsLaborTicketResponse';
import { throwWarningToasts } from 'lib/utils/toast';

const useHandleNatsLaborTicketResponse = () => {
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const workOrderOperationLaborTickets = useSelector(
    getWorkOrderOperationLaborTickets
  );
  const resyncLaborTickets = useResyncMachineLaborTickets();

  return useCallback(
    async ({ response, options = {} }) => {
      const { laborTickets } = dispatchNatsResponseActions({
        response,
        dispatch,
        machine,
        options,
      });

      // Not every caller checks `success` first; never toast warnings next to a
      // failed submission.
      if (response?.success !== false) {
        throwWarningToasts(response?.warnings);
      }

      if (laborTickets.length > 0) {
        const primaryTicket = laborTickets[0];
        dispatch(actionSetLaborTickets([primaryTicket]));

        if (primaryTicket.laborTicketRef) {
          const currentList = workOrderOperationLaborTickets || [];
          const merged = currentList.filter((t) => {
            return t.laborTicketRef !== primaryTicket.laborTicketRef;
          });
          merged.push(primaryTicket);
          dispatch(actionSetWorkOrderOperationLaborTickets(merged));
        }

        try {
          await resyncLaborTickets();
        } catch {
          // Let the subscription reconcile if immediate resync fails.
        }
      }

      return { laborTickets };
    },
    [dispatch, machine, workOrderOperationLaborTickets, resyncLaborTickets]
  );
};

export default useHandleNatsLaborTicketResponse;
